/*
  Pixelpa — Color Panel component.
  Shows current fg/bg colors, color sliders (RGB, HSB, HSL, Lab, CMYK,
  Grayscale, Web-Color), hex input, and quick actions (swap, reset).

  Closes #33 — adds HSB, Lab, Grayscale, Web Color mode tabs.
*/

import type { RGB } from "../../core/tools/Tool";
import { rgbToHsv, hsvToRgb, rgbToHex, hexToRgb } from "./ColorPicker";
import {
  convertRGBtoLab, convertLabtoRGB,
  convertRGBtoCMYK, convertCMYKtoRGB,
  convertRGBtoGrayscale, convertGrayscaleToRGB,
  convertRGBtoHSB, convertHSBtoRGB,
} from "../../core/color/IccProfile";

interface HSL { h: number; s: number; l: number; }

function rgbToHsl(r: number, g: number, b: number): HSL {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else h = ((rn - gn) / d + 4) / 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb(h: number, s: number, l: number): RGB {
  const hn = ((h % 360) + 360) % 360 / 360;
  const sn = Math.max(0, Math.min(100, s)) / 100;
  const ln = Math.max(0, Math.min(100, l)) / 100;
  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
  const p = 2 * ln - q;
  return {
    r: Math.round(hue2rgb(p, q, hn + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, hn) * 255),
    b: Math.round(hue2rgb(p, q, hn - 1 / 3) * 255),
  };
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

export type ColorPanelMode = "rgb" | "hsb" | "hsl" | "lab" | "cmyk" | "grayscale" | "web";

export class ColorPanel {
  private root: HTMLDivElement;
  private fgSwatch: HTMLDivElement;
  private bgSwatch: HTMLDivElement;
  private hexInput: HTMLInputElement;
  private previewSwatch: HTMLDivElement;
  private modeButtons: HTMLButtonElement[];
  private currentMode: ColorPanelMode = "rgb";
  private rgbSliders: HTMLDivElement;
  private hsbSliders: HTMLDivElement;
  private hslSliders: HTMLDivElement;
  private labSliders: HTMLDivElement;
  private cmykSliders: HTMLDivElement;
  private grayscaleSliders: HTMLDivElement;
  private webColorSection: HTMLDivElement;

  private _fgColor: RGB = { r: 0, g: 0, b: 0 };
  private _bgColor: RGB = { r: 255, g: 255, b: 255 };
  private _onFgChange: ((c: RGB) => void) | null = null;
  private _onBgChange: ((c: RGB) => void) | null = null;
  private _suppressSync = false;

  constructor() {
    this.root = el("div", "color-panel");

    // Foreground / Background swatches
    const swatchRow = el("div", "color-panel__swatches");
    const fgWrap = el("div", "color-panel__swatch-wrap");
    const fgLabel = el("div", "color-panel__swatch-label");
    fgLabel.textContent = "Foreground";
    this.fgSwatch = el("div", "color-panel__swatch");
    fgWrap.appendChild(fgLabel);
    fgWrap.appendChild(this.fgSwatch);

    const bgWrap = el("div", "color-panel__swatch-wrap");
    const bgLabel = el("div", "color-panel__swatch-label");
    bgLabel.textContent = "Background";
    this.bgSwatch = el("div", "color-panel__swatch");
    bgWrap.appendChild(bgLabel);
    bgWrap.appendChild(this.bgSwatch);

    const swapBtn = el("button", "color-panel__swap");
    swapBtn.textContent = "⇄";
    swapBtn.title = "Swap (X)";
    swapBtn.addEventListener("click", () => this.swapColors());

    const resetBtn = el("button", "color-panel__reset");
    resetBtn.textContent = "D";
    resetBtn.title = "Default (D)";
    resetBtn.addEventListener("click", () => this.resetColors());

    swatchRow.appendChild(fgWrap);
    swatchRow.appendChild(bgWrap);
    swatchRow.appendChild(swapBtn);
    swatchRow.appendChild(resetBtn);
    this.root.appendChild(swatchRow);

    // Mode buttons — RGB, HSB, HSL, Lab, CMYK, Grayscale, Web Color
    const modeRow = el("div", "color-panel__modes");
    this.modeButtons = [];
    const MODES: { id: ColorPanelMode; label: string }[] = [
      { id: "rgb",       label: "RGB"  },
      { id: "hsb",       label: "HSB"  },
      { id: "hsl",       label: "HSL"  },
      { id: "lab",       label: "Lab"  },
      { id: "cmyk",      label: "CMYK" },
      { id: "grayscale", label: "Gray" },
      { id: "web",       label: "Web"  },
    ];
    for (const m of MODES) {
      const btn = el("button", "color-panel__mode");
      btn.textContent = m.label;
      btn.dataset.mode = m.id;
      btn.addEventListener("click", () => this.setMode(m.id));
      this.modeButtons.push(btn);
      modeRow.appendChild(btn);
    }
    this.modeButtons[0]!.classList.add("is-active");
    this.root.appendChild(modeRow);

    // RGB sliders
    this.rgbSliders = el("div", "color-panel__sliders");
    this.rgbSliders.appendChild(this.makeSliderRow("R", 255, (v) => { this._fgColor = { ...this._fgColor, r: v }; this.notifyFg(); }));
    this.rgbSliders.appendChild(this.makeSliderRow("G", 255, (v) => { this._fgColor = { ...this._fgColor, g: v }; this.notifyFg(); }));
    this.rgbSliders.appendChild(this.makeSliderRow("B", 255, (v) => { this._fgColor = { ...this._fgColor, b: v }; this.notifyFg(); }));
    this.root.appendChild(this.rgbSliders);

    // HSB sliders
    this.hsbSliders = el("div", "color-panel__sliders");
    this.hsbSliders.style.display = "none";
    this.hsbSliders.appendChild(this.makeSliderRow("H", 360, (v) => {
      const hsb = convertRGBtoHSB(this._fgColor.r, this._fgColor.g, this._fgColor.b);
      const rgb = convertHSBtoRGB(v, hsb.s, hsb.b);
      this._fgColor = rgb; this.notifyFg();
    }));
    this.hsbSliders.appendChild(this.makeSliderRow100("S%", (v) => {
      const hsb = convertRGBtoHSB(this._fgColor.r, this._fgColor.g, this._fgColor.b);
      const rgb = convertHSBtoRGB(hsb.h, v / 100, hsb.b);
      this._fgColor = rgb; this.notifyFg();
    }));
    this.hsbSliders.appendChild(this.makeSliderRow100("B%", (v) => {
      const hsb = convertRGBtoHSB(this._fgColor.r, this._fgColor.g, this._fgColor.b);
      const rgb = convertHSBtoRGB(hsb.h, hsb.s, v / 100);
      this._fgColor = rgb; this.notifyFg();
    }));
    this.root.appendChild(this.hsbSliders);

    // HSL sliders
    this.hslSliders = el("div", "color-panel__sliders");
    this.hslSliders.style.display = "none";
    this.hslSliders.appendChild(this.makeSliderRow("H", 360, (v) => { const hsl = rgbToHsl(this._fgColor.r, this._fgColor.g, this._fgColor.b); const rgb = hslToRgb(v, hsl.s, hsl.l); this._fgColor = rgb; this.notifyFg(); }));
    this.hslSliders.appendChild(this.makeSliderRow("S", 100, (v) => { const hsl = rgbToHsl(this._fgColor.r, this._fgColor.g, this._fgColor.b); const rgb = hslToRgb(hsl.h, v, hsl.l); this._fgColor = rgb; this.notifyFg(); }));
    this.hslSliders.appendChild(this.makeSliderRow("L", 100, (v) => { const hsl = rgbToHsl(this._fgColor.r, this._fgColor.g, this._fgColor.b); const rgb = hslToRgb(hsl.h, hsl.s, v); this._fgColor = rgb; this.notifyFg(); }));
    this.root.appendChild(this.hslSliders);

    // Lab sliders: L [0..100], a [-128..127], b [-128..127]
    this.labSliders = el("div", "color-panel__sliders");
    this.labSliders.style.display = "none";
    this.labSliders.appendChild(this.makeLabSliderRow("L*", 0, 100, (v) => {
      const lab = convertRGBtoLab(this._fgColor.r, this._fgColor.g, this._fgColor.b);
      const rgb = convertLabtoRGB(v, lab.a, lab.b);
      this._fgColor = rgb; this.notifyFg();
    }));
    this.labSliders.appendChild(this.makeLabSliderRow("a*", -128, 127, (v) => {
      const lab = convertRGBtoLab(this._fgColor.r, this._fgColor.g, this._fgColor.b);
      const rgb = convertLabtoRGB(lab.L, v, lab.b);
      this._fgColor = rgb; this.notifyFg();
    }));
    this.labSliders.appendChild(this.makeLabSliderRow("b*", -128, 127, (v) => {
      const lab = convertRGBtoLab(this._fgColor.r, this._fgColor.g, this._fgColor.b);
      const rgb = convertLabtoRGB(lab.L, lab.a, v);
      this._fgColor = rgb; this.notifyFg();
    }));
    this.root.appendChild(this.labSliders);

    // CMYK sliders
    this.cmykSliders = el("div", "color-panel__sliders");
    this.cmykSliders.style.display = "none";
    this.cmykSliders.appendChild(this.makeSliderRow("C", 100, (v) => { this._fgColor = this.cmykToRgb(v, this._cmykM, this._cmykY, this._cmykK); this.notifyFg(); }));
    this.cmykSliders.appendChild(this.makeSliderRow("M", 100, (v) => { this._cmykM = v; this._fgColor = this.cmykToRgb(this._cmykC, v, this._cmykY, this._cmykK); this.notifyFg(); }));
    this.cmykSliders.appendChild(this.makeSliderRow("Y", 100, (v) => { this._cmykY = v; this._fgColor = this.cmykToRgb(this._cmykC, this._cmykM, v, this._cmykK); this.notifyFg(); }));
    this.cmykSliders.appendChild(this.makeSliderRow("K", 100, (v) => { this._cmykK = v; this._fgColor = this.cmykToRgb(this._cmykC, this._cmykM, this._cmykY, v); this.notifyFg(); }));
    this.root.appendChild(this.cmykSliders);

    // Grayscale slider
    this.grayscaleSliders = el("div", "color-panel__sliders");
    this.grayscaleSliders.style.display = "none";
    this.grayscaleSliders.appendChild(this.makeSliderRow("K", 255, (v) => {
      const rgb = convertGrayscaleToRGB(v);
      this._fgColor = rgb; this.notifyFg();
    }));
    this.root.appendChild(this.grayscaleSliders);

    // Web Color swatches (CSS named colors)
    this.webColorSection = el("div", "color-panel__web");
    this.webColorSection.style.display = "none";
    this._buildWebColorSwatches();
    this.root.appendChild(this.webColorSection);

    // Hex input
    const hexRow = el("div", "color-panel__hex");
    const hexLabel = el("label");
    hexLabel.textContent = "#";
    this.hexInput = el("input");
    this.hexInput.type = "text";
    this.hexInput.maxLength = 6;
    this.hexInput.spellcheck = false;
    this.hexInput.addEventListener("change", () => {
      const rgb = hexToRgb(this.hexInput.value);
      if (rgb) { this._fgColor = rgb; this.notifyFg(); this.syncAll(); }
    });
    hexRow.appendChild(hexLabel);
    hexRow.appendChild(this.hexInput);
    this.root.appendChild(hexRow);

    // Preview
    const previewRow = el("div", "color-panel__preview");
    this.previewSwatch = el("div", "color-panel__preview-swatch");
    previewRow.appendChild(this.previewSwatch);
    this.root.appendChild(previewRow);

    // Swatch click handlers
    this.fgSwatch.addEventListener("click", () => this._onFgChange?.(this._fgColor));
    this.bgSwatch.addEventListener("click", (e) => {
      if (e.altKey) this._onBgChange?.(this._bgColor);
      else this._onBgChange?.(this._bgColor);
    });
  }

  private _cmykC = 0;
  private _cmykM = 0;
  private _cmykY = 0;
  private _cmykK = 0;

  private cmykToRgb(c: number, m: number, y: number, k: number): RGB {
    return convertCMYKtoRGB(c, m, y, k);
  }

  private rgbToCmyk(rgb: RGB): { c: number; m: number; y: number; k: number } {
    return convertRGBtoCMYK(rgb.r, rgb.g, rgb.b);
  }

  /** Slider for 0..max (integer) */
  private makeSliderRow(label: string, max: number, onChange: (v: number) => void): HTMLDivElement {
    const row = el("div", "color-panel__slider-row");
    const lab = el("label", "color-panel__slider-label");
    lab.textContent = label;
    const slider = el("input");
    slider.type = "range";
    slider.min = "0";
    slider.max = String(max);
    slider.value = "0";
    const val = el("span", "color-panel__slider-value");
    val.textContent = "0";
    slider.addEventListener("input", () => {
      const v = parseInt(slider.value, 10);
      val.textContent = String(v);
      onChange(v);
      this.syncAll();
    });
    row.appendChild(lab);
    row.appendChild(slider);
    row.appendChild(val);
    return row;
  }

  /** Slider for 0..100 (percentage, useful for HSB S% and B%) */
  private makeSliderRow100(label: string, onChange: (v: number) => void): HTMLDivElement {
    return this.makeSliderRow(label, 100, onChange);
  }

  /** Slider for Lab channels — signed range [min..max] */
  private makeLabSliderRow(label: string, min: number, max: number, onChange: (v: number) => void): HTMLDivElement {
    const row = el("div", "color-panel__slider-row");
    const lab = el("label", "color-panel__slider-label");
    lab.textContent = label;
    const slider = el("input");
    slider.type = "range";
    slider.min = String(min);
    slider.max = String(max);
    slider.value = "0";
    const val = el("span", "color-panel__slider-value");
    val.textContent = "0";
    slider.addEventListener("input", () => {
      const v = parseInt(slider.value, 10);
      val.textContent = String(v);
      onChange(v);
      this.syncAll();
    });
    row.appendChild(lab);
    row.appendChild(slider);
    row.appendChild(val);
    return row;
  }

  /** Build the web-color swatch grid (CSS named colours) */
  private _buildWebColorSwatches(): void {
    const WEB_COLORS: [string, string][] = [
      ["Black",   "#000000"], ["White",   "#ffffff"], ["Red",     "#ff0000"],
      ["Lime",    "#00ff00"], ["Blue",    "#0000ff"], ["Yellow",  "#ffff00"],
      ["Cyan",    "#00ffff"], ["Magenta", "#ff00ff"], ["Silver",  "#c0c0c0"],
      ["Gray",    "#808080"], ["Maroon",  "#800000"], ["Olive",   "#808000"],
      ["Green",   "#008000"], ["Purple",  "#800080"], ["Teal",    "#008080"],
      ["Navy",    "#000080"], ["Orange",  "#ffa500"], ["Gold",    "#ffd700"],
      ["Coral",   "#ff7f50"], ["Salmon",  "#fa8072"], ["Tomato",  "#ff6347"],
      ["Crimson", "#dc143c"], ["IndianRed","#cd5c5c"],["FireBrick","#b22222"],
    ];
    const title = el("div", "color-panel__web-title");
    title.textContent = "Web Named Colors";
    this.webColorSection.appendChild(title);
    const grid = el("div", "color-panel__web-grid");
    for (const [name, hex] of WEB_COLORS) {
      const sw = el("div", "color-panel__web-swatch");
      sw.style.background = hex;
      sw.title = name;
      sw.addEventListener("click", () => {
        const rgb = hexToRgb(hex.slice(1));
        if (rgb) { this._fgColor = rgb; this.notifyFg(); this.syncAll(); }
      });
      grid.appendChild(sw);
    }
    this.webColorSection.appendChild(grid);
  }

  setMode(mode: ColorPanelMode): void {
    this.currentMode = mode;
    this.rgbSliders.style.display      = mode === "rgb"       ? "" : "none";
    this.hsbSliders.style.display      = mode === "hsb"       ? "" : "none";
    this.hslSliders.style.display      = mode === "hsl"       ? "" : "none";
    this.labSliders.style.display      = mode === "lab"       ? "" : "none";
    this.cmykSliders.style.display     = mode === "cmyk"      ? "" : "none";
    this.grayscaleSliders.style.display = mode === "grayscale" ? "" : "none";
    this.webColorSection.style.display = mode === "web"       ? "" : "none";
    this.modeButtons.forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.mode === mode);
    });
    this.syncAll();
  }

  set fgColor(c: RGB) {
    this._fgColor = { ...c };
    this.syncAll();
  }

  set bgColor(c: RGB) {
    this._bgColor = { ...c };
    this.syncAll();
  }

  setCallbacks(callbacks: { onFgColorChange: (c: RGB) => void; onBgColorChange: (c: RGB) => void }): void {
    this._onFgChange = callbacks.onFgColorChange;
    this._onBgChange = callbacks.onBgColorChange;
  }

  private notifyFg(): void {
    if (this._suppressSync) return;
    this._onFgChange?.(this._fgColor);
    this.syncAll();
  }

  syncAll(): void {
    this._suppressSync = true;
    this.fgSwatch.style.background = `rgb(${this._fgColor.r},${this._fgColor.g},${this._fgColor.b})`;
    this.bgSwatch.style.background = `rgb(${this._bgColor.r},${this._bgColor.g},${this._bgColor.b})`;
    this.hexInput.value = rgbToHex(this._fgColor.r, this._fgColor.g, this._fgColor.b);
    this.previewSwatch.style.background = `rgb(${this._fgColor.r},${this._fgColor.g},${this._fgColor.b})`;

    // RGB sliders
    const rgbSliders = this.rgbSliders.querySelectorAll<HTMLInputElement>("input[type=range]");
    const rgbValues = this.rgbSliders.querySelectorAll<HTMLSpanElement>(".color-panel__slider-value");
    if (rgbSliders[0]) { rgbSliders[0].value = String(this._fgColor.r); rgbValues[0]!.textContent = String(this._fgColor.r); }
    if (rgbSliders[1]) { rgbSliders[1].value = String(this._fgColor.g); rgbValues[1]!.textContent = String(this._fgColor.g); }
    if (rgbSliders[2]) { rgbSliders[2].value = String(this._fgColor.b); rgbValues[2]!.textContent = String(this._fgColor.b); }

    // HSB sliders
    const hsb = convertRGBtoHSB(this._fgColor.r, this._fgColor.g, this._fgColor.b);
    const hsbSliders = this.hsbSliders.querySelectorAll<HTMLInputElement>("input[type=range]");
    const hsbValues = this.hsbSliders.querySelectorAll<HTMLSpanElement>(".color-panel__slider-value");
    if (hsbSliders[0]) { hsbSliders[0].value = String(Math.round(hsb.h)); hsbValues[0]!.textContent = String(Math.round(hsb.h)); }
    if (hsbSliders[1]) { hsbSliders[1].value = String(Math.round(hsb.s * 100)); hsbValues[1]!.textContent = String(Math.round(hsb.s * 100)); }
    if (hsbSliders[2]) { hsbSliders[2].value = String(Math.round(hsb.b * 100)); hsbValues[2]!.textContent = String(Math.round(hsb.b * 100)); }

    // HSL sliders
    const hsl = rgbToHsl(this._fgColor.r, this._fgColor.g, this._fgColor.b);
    const hslSliders = this.hslSliders.querySelectorAll<HTMLInputElement>("input[type=range]");
    const hslValues = this.hslSliders.querySelectorAll<HTMLSpanElement>(".color-panel__slider-value");
    if (hslSliders[0]) { hslSliders[0].value = String(Math.round(hsl.h)); hslValues[0]!.textContent = String(Math.round(hsl.h)); }
    if (hslSliders[1]) { hslSliders[1].value = String(Math.round(hsl.s)); hslValues[1]!.textContent = String(Math.round(hsl.s)); }
    if (hslSliders[2]) { hslSliders[2].value = String(Math.round(hsl.l)); hslValues[2]!.textContent = String(Math.round(hsl.l)); }

    // Lab sliders
    const lab = convertRGBtoLab(this._fgColor.r, this._fgColor.g, this._fgColor.b);
    const labSliders = this.labSliders.querySelectorAll<HTMLInputElement>("input[type=range]");
    const labValues = this.labSliders.querySelectorAll<HTMLSpanElement>(".color-panel__slider-value");
    if (labSliders[0]) { labSliders[0].value = String(Math.round(lab.L)); labValues[0]!.textContent = String(Math.round(lab.L)); }
    if (labSliders[1]) { labSliders[1].value = String(Math.round(lab.a)); labValues[1]!.textContent = String(Math.round(lab.a)); }
    if (labSliders[2]) { labSliders[2].value = String(Math.round(lab.b)); labValues[2]!.textContent = String(Math.round(lab.b)); }

    // CMYK sliders
    const cmyk = this.rgbToCmyk(this._fgColor);
    this._cmykC = cmyk.c; this._cmykM = cmyk.m; this._cmykY = cmyk.y; this._cmykK = cmyk.k;
    const cmykSliders = this.cmykSliders.querySelectorAll<HTMLInputElement>("input[type=range]");
    const cmykValues = this.cmykSliders.querySelectorAll<HTMLSpanElement>(".color-panel__slider-value");
    if (cmykSliders[0]) { cmykSliders[0].value = String(cmyk.c); cmykValues[0]!.textContent = String(cmyk.c); }
    if (cmykSliders[1]) { cmykSliders[1].value = String(cmyk.m); cmykValues[1]!.textContent = String(cmyk.m); }
    if (cmykSliders[2]) { cmykSliders[2].value = String(cmyk.y); cmykValues[2]!.textContent = String(cmyk.y); }
    if (cmykSliders[3]) { cmykSliders[3].value = String(cmyk.k); cmykValues[3]!.textContent = String(cmyk.k); }

    // Grayscale slider
    const gray = convertRGBtoGrayscale(this._fgColor.r, this._fgColor.g, this._fgColor.b);
    const graySliders = this.grayscaleSliders.querySelectorAll<HTMLInputElement>("input[type=range]");
    const grayValues = this.grayscaleSliders.querySelectorAll<HTMLSpanElement>(".color-panel__slider-value");
    if (graySliders[0]) { graySliders[0].value = String(gray); grayValues[0]!.textContent = String(gray); }

    this._suppressSync = false;
  }

  swapColors(): void {
    const tmp = { ...this._fgColor };
    this._fgColor = { ...this._bgColor };
    this._bgColor = tmp;
    this._onFgChange?.(this._fgColor);
    this._onBgChange?.(this._bgColor);
    this.syncAll();
  }

  resetColors(): void {
    this._fgColor = { r: 0, g: 0, b: 0 };
    this._bgColor = { r: 255, g: 255, b: 255 };
    this._onFgChange?.(this._fgColor);
    this._onBgChange?.(this._bgColor);
    this.syncAll();
  }

  mount(container: HTMLElement): void {
    // Clear any pre-existing markup (e.g. the docked `.color-body` panel's
    // static simple RGB/hex slider markup from index.html) so there's only
    // ever one authoritative color UI in the container — mounting the rich
    // ColorPanel must fully replace it, not sit alongside/behind it (i126).
    container.innerHTML = "";
    container.appendChild(this.root);
    this.syncAll();
  }

  destroy(): void {
    this.root.remove();
  }
}
